// Copyright (c) 2021 WiseTime. All rights reserved.

import React from "react"
import SearchDropdown from "features/shared/search-dropdown/SearchDropdown"
import SortDropdown from "features/shared/search-dropdown/SortDropdown"
import { useTagsDropdown } from "./useTagsDropdown"
import { SearchTagsRequest } from "generated/legebill/site/tag/v4/tag_pb"
import { Option } from "features/shared/Dropdown"
import { RecentTagsOrderBy } from "./slice"
import { Destination } from "../shared/search-dropdown/types"
import { t } from "@lingui/macro"
import { TagRef } from "../../generated/wisetime/user/type/tag_ref_pb"
import { Placement } from "@floating-ui/react-dom"

export const sortOptions: Option[] = [
  {
    value: SearchTagsRequest.OrderBy.NAME,
    label: t({ id: "label_alphabetical", message: "Alphabetical" }),
  },
  {
    value: SearchTagsRequest.OrderBy.RELEVANCE,
    label: t({ id: "label_relevance", message: "Relevance" }),
  },
]

const sortRecentOptions: Option[] = [
  {
    value: RecentTagsOrderBy.NAME,
    label: t({ id: "label_alphabetical", message: "Alphabetical" }),
  },
  {
    value: RecentTagsOrderBy.RELEVANCE,
    label: t({ id: "label_relevance", message: "Relevance" }),
  },
]

type Props = {
  placement?: Placement,
  timeGroupsIds?: Destination,
  timeRowsIds?: Destination,
  addedItemIds?: string[],
  icon: string,
  isTimeRow?: boolean,
  isFocusable?: boolean,
  className?: string,
  stopPropagation?: boolean,
  onItemSelected?: (tagRef: TagRef) => void,
  onFocus?: () => void,
}

const TagsDropdown = ({
  placement = "bottom-end",
  timeGroupsIds = [],
  timeRowsIds = [],
  addedItemIds = [],
  icon,
  isTimeRow = false,
  isFocusable = false,
  className,
  stopPropagation = true,
  onItemSelected,
  onFocus,
}: Props): React.ReactElement<Props> => {
  const {
    isSearchingTags,
    favouriteTagsList,
    recentTagsList,
    resultTagsList,
    tagsSearchOrderBy,
    tagsRecentOrderBy,
    tagsViewMode,
    onAddTagToDestination,
    onSearchTags,
    onResetSearchTags,
    onCreateFavouriteTag,
    onDeleteFavouriteTag,
    onReorderFavouriteTag,
    onChangeTagsOrderBy,
    onChangeRecentTagsOrderBy,
    onChangeTagsViewMode,
    onFetchTags,
  } = useTagsDropdown({ onItemSelected })
  return (
    <SearchDropdown
      placement={placement}
      className={className}
      stopPropagation={stopPropagation}
      timeGroupsIds={timeGroupsIds}
      timeRowsIds={timeRowsIds}
      addedItemIds={addedItemIds}
      icon={icon}
      searchSortDropdown={(
        <SortDropdown
          value={tagsSearchOrderBy}
          options={sortOptions}
          onChange={onChangeTagsOrderBy}
        />
      )}
      recentSortDropdown={(
        <SortDropdown
          value={tagsRecentOrderBy}
          options={sortRecentOptions}
          onChange={onChangeRecentTagsOrderBy}
          isRecentList
        />
      )}
      orderBy={tagsSearchOrderBy}
      title={t({ id: "label_add_tag", message: "Add tag" })}
      placeholder={t({ id: "label_search_tags", message: "Search tags" })}
      favouritesList={favouriteTagsList}
      recentList={recentTagsList}
      searchResultList={resultTagsList}
      viewMode={tagsViewMode}
      isTimeRow={isTimeRow}
      isFocusable={isFocusable}
      isSearching={isSearchingTags}
      onSearchItems={onSearchTags}
      onResetSearchItems={onResetSearchTags}
      onAddItemToDestination={onAddTagToDestination}
      onFavouriteItem={onCreateFavouriteTag}
      onUnfavouriteItem={onDeleteFavouriteTag}
      onChangeViewMode={onChangeTagsViewMode}
      onReorderDraggableItem={onReorderFavouriteTag}
      onFetchItems={onFetchTags}
      onFocus={onFocus}
      showFavouriteButton
    />
  )
}

export default TagsDropdown
